import React, { useState } from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography, Divider } from '@mui/material';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { baseUrl } from '../../../util/constant';
import PreviewPicture from '../../../components/PreviewPic/PreviewPicture';

const imageFormats = ['.jpg', '.jpeg', '.png', '.gif'];
const isImage = (f) => {
  const ext = String(f?.extension || '').toLowerCase();
  return imageFormats.some((fmt) => ext.includes(fmt));
};
const vehicleTitle = (v) => [v?.vehicleType, v?.year, v?.make, v?.model].filter(Boolean).join(' ');
const vehicleId = (v) => v?.vin || v?.chassisNumber || v?.chassisNo || v?.serial || '-';

const VehicleThumb = ({ v, onPreview, containerLabel, layout = 'row' }) => {
  const pictures = v?._pictures || [];
  const first = pictures.find(isImage);
  const title = vehicleTitle(v) || 'Vehicle';
  const id = vehicleId(v);
  const color = v?.color || v?.color || null;
  const year = v?.year || null;
  const fuelType = v?.fuelType || v?.price || null;
  
  // Compact card for horizontal strip (prevents 100% width items on mobile)
  if (layout === 'tile') {
    return (
      <Box sx={{
        width: { xs: 140, sm: 160 },
        flex: '0 0 auto',
        borderRadius: 1.25,
        border: '1px solid #e5e7eb',
        bgcolor: '#f8fafc',
        p: 0.75
      }}>
        <Box sx={{ position: 'relative', width: '100%', height: { xs: 84, sm: 96 }, borderRadius: 1, overflow: 'hidden', border: '1px solid #e5e7eb', bgcolor: '#fff' }}>
          <Box sx={{ position: 'absolute', top: 2, right: 2, zIndex: 1 }}>
            <Tooltip title={pictures.length ? 'View images' : 'No images'}>
              <span>
                <IconButton size="small" onClick={() => onPreview && onPreview(pictures)} disabled={!pictures.length} sx={{ bgcolor: '#ffffffcc' }}>
                  <PhotoLibraryIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {containerLabel && (
            <Box sx={{ position: 'absolute', left: 4, bottom: 4, bgcolor: '#0B3C5D', color: '#fff', px: 0.75, py: 0.25, borderRadius: 0.75, fontSize: 10, opacity: 0.9 }}>
              {containerLabel}
            </Box>
          )}
          {first ? (
            <img src={`${baseUrl}${first.filePath}`} alt={first.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
              <Typography variant="caption">No image</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 0.75, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{title}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{id}</Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} alignItems="center">
            {year && <Typography variant="caption" color="text.secondary">{year}</Typography>}
            {color && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: String(color).toLowerCase(), border: '1px solid #e0e0e0' }} />
                <Typography variant="caption" color="text.secondary" noWrap>{color}</Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>
    );
  }

  // Default: full-width row layout for list view
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.25, border: '1px solid #e5e7eb', bgcolor: '#f8fafc', minHeight: 80, width: '100%', maxWidth: '100%', flex: '1 1 auto' }}>
      <Box sx={{ position: 'relative', width: { xs: 72, sm: 96 }, height: { xs: 48, sm: 64 }, borderRadius: 1, overflow: 'hidden', border: '1px solid #e5e7eb', bgcolor: '#fff', flexShrink: 0 }}>
        <Box sx={{ position: 'absolute', top: 2, right: 2, zIndex: 1 }}>
          <Tooltip title={pictures.length ? 'View images' : 'No images'}>
            <span>
              <IconButton size="small" onClick={() => onPreview && onPreview(pictures)} disabled={!pictures.length} sx={{ bgcolor: '#ffffffcc' }}>
                <PhotoLibraryIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {first ? (
          <img src={`${baseUrl}${first.filePath}`} alt={first.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
            <Typography variant="caption">No image</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{title}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>{id}</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
          {year && (
            <Typography variant="caption" color="text.secondary">{year}</Typography>
          )}
          {color && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: String(color).toLowerCase(), border: '1px solid #e0e0e0' }} />
              <Typography variant="caption" color="text.secondary" noWrap>{color}</Typography>
            </Stack>
          )}
        </Stack>
      </Box>
      <Box sx={{ ml: 'auto', flexShrink: 0 }}>
        {fuelType && (
          <Chip size="small" color="info" label={`${fuelType.toUpperCase()}`} />
        )}
      </Box>
    </Box>
  );
};

const ContainerRow = ({ c, vehicleLimit, expanded, onToggleExpand, onPreview }) => {



  const vehicles = (c?.shipmentItems || []).map((v) => ({ ...v.vehicle, _pictures: v?.vehicle?.warehousePictures || v?.vehicle?.picture || v?.vehicle?.pictures || [] }));
  const list = expanded ? vehicles : vehicles.slice(0, vehicleLimit);
  const hidden = Math.max((vehicles.length - list.length), 0);
  const cNum = c?.containerNumber || 'N/A';
  const seal = c?.sealNumber || '-';

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Inventory2Icon sx={{ color: '#0B3C5D', mt: '2px' }} fontSize="small" />
        <Box sx={{ display: 'grid', flex: 1, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, columnGap: 1, rowGap: 0.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption"><strong>Number:</strong></Typography>
            <Typography variant="body2" noWrap>{cNum || '-'}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption"><strong>Seal:</strong></Typography>
            <Typography variant="body2" noWrap>{seal || '-'}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption"><strong>Vehicles:</strong></Typography>
            <Typography variant="body2">{vehicles.length || 0}</Typography>
          </Stack>
        </Box>
      </Box>




    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pb: 0.5 ,pt:1}}>
        {list.map((v, i) => (
          <React.Fragment key={v.id || v.vin || v.chassisNumber || i}>
            <VehicleThumb v={v} onPreview={onPreview} layout="row" containerLabel={`#${cNum}`} />
            {i < list.length - 1 && <Divider sx={{ my: 0.25 }} />}
          </React.Fragment>
        ))}
        {hidden > 0 && (
      <Chip size="small" label={`+${hidden} more`} onClick={onToggleExpand} sx={{ alignSelf: 'center' }} />
        )}
      </Box>
    </Box>
  );
};

const JobsContainersGroup = ({ job, containerLimit = 2, vehicleLimitPerContainer = 3, singleRow = false, vehicleLimit = 10 }) => {
  const [expandedContainers, setExpandedContainers] = useState({});
  const [showAllContainers, setShowAllContainers] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);

  const containers = job?.containers || [];
  const visibleContainers = showAllContainers ? containers : containers.slice(0, containerLimit);

  const hiddenContainers = Math.max(containers.length - visibleContainers.length, 0);

  const handleToggleContainer = (key) => setExpandedContainers((m) => ({ ...m, [key]: !m[key] }));
  const handleOpenPreview = (files) => { setPreviewFiles(files || []); setPreviewOpen(true); };
  const handleClosePreview = () => { setPreviewOpen(false); setPreviewFiles([]); };

  if (!containers.length) return <Typography variant="caption" color="text.secondary">No containers</Typography>;

  if (singleRow) {
    const allVehicles = containers.flatMap((c) => (c?.shipmentItems || []).map((v) => ({ ...v.vehicle, _pictures: v?.vehicle?.warehousePictures || v?.vehicle?.picture || v?.vehicle?.pictures || [], _containerNumber: c?.containerNumber })));
    const list = allVehicles.slice(0, vehicleLimit);
    const hidden = Math.max(allVehicles.length - list.length, 0);
    return (
      <Box sx={{ mt: 1 }}>
    <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.5, flexWrap: 'nowrap' }}>
          {list.map((v, i) => (
      <VehicleThumb key={v.id || v.vin || v.chassisNumber || i} v={v} onPreview={handleOpenPreview} containerLabel={`#${v._containerNumber || 'N/A'}`} layout="tile" />
          ))}
          {hidden > 0 && (
            <Chip size="small" label={`+${hidden} more`} />
          )}
        </Box>
        {previewOpen && (
          <PreviewPicture open={previewOpen} handleClose={handleClosePreview} label={'Vehicle Pictures'} file={previewFiles} />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {visibleContainers.map((c, idx) => {
        const key = c.id || c.containerNumber || idx;
        const expanded = !!expandedContainers[key];
        return (
          <ContainerRow
            key={key}
            c={c}
            vehicleLimit={vehicleLimitPerContainer}
            expanded={expanded}
            onToggleExpand={() => handleToggleContainer(key)}
            onPreview={handleOpenPreview}
          />
        );
      })}
      {hiddenContainers > 0 && (
        <Chip size="small" label={`+${hiddenContainers} more containers`} onClick={() => setShowAllContainers(true)} />
      )}
      {previewOpen && (
        <PreviewPicture open={previewOpen} handleClose={handleClosePreview} label={'Vehicle Pictures'} file={previewFiles} />
      )}
    </Box>
  );
};

export default JobsContainersGroup;
